import { useState, useEffect, useCallback, useMemo } from 'react'
import { loadMemory, saveMemory, clearMemory, parseMemoryToStructured } from '../services/memoryStorage'
import { ResumeMemory, CompletionStatus } from '../types/memory'

interface UseMemoryReturn {
  memory: string
  structuredMemory: ResumeMemory
  setMemory: (memory: string) => void
  updateMemory: (newMemory: string | null) => void
  resetMemory: () => void
  getCompletionStatus: () => CompletionStatus
}

export function useMemory(): UseMemoryReturn {
  const [memory, setMemoryState] = useState<string>(() => loadMemory())

  // Persist to localStorage whenever memory changes
  useEffect(() => {
    saveMemory(memory)
  }, [memory])

  // Compute structured memory from text
  const structuredMemory = useMemo(() => {
    return parseMemoryToStructured(memory)
  }, [memory])

  const setMemory = useCallback((newMemory: string) => {
    setMemoryState(newMemory)
  }, [])

  // Update memory only if new value is provided (not null)
  const updateMemory = useCallback((newMemory: string | null) => {
    if (newMemory !== null) {
      setMemoryState(newMemory)
    }
  }, [])

  const resetMemory = useCallback(() => {
    clearMemory()
    setMemoryState('')
  }, [])

  // Calculate completion status
  const getCompletionStatus = useCallback((): CompletionStatus => {
    const structured = parseMemoryToStructured(memory)

    // Target completeness
    const targetMissing: string[] = []
    if (!structured.target.role) targetMissing.push('role')
    if (!structured.target.companyTier) targetMissing.push('company tier')
    if (!structured.target.industry) targetMissing.push('industry')
    if (!structured.target.level) targetMissing.push('level')
    const targetComplete = targetMissing.length === 0

    // Contact completeness
    const contactMissing: string[] = []
    if (!structured.contact.name) contactMissing.push('name')
    if (!structured.contact.email) contactMissing.push('email')
    if (!structured.contact.phone) contactMissing.push('phone')
    if (!structured.contact.location) contactMissing.push('location')
    // LinkedIn and GitHub are optional, not required
    const contactComplete = contactMissing.length === 0

    // Education completeness (at least one entry)
    const educationComplete = structured.education.length > 0
    const educationCount = structured.education.length

    // Experience analysis
    let strongBullets = 0
    let weakBullets = 0
    let needsWorkBullets = 0

    structured.experience.forEach(exp => {
      exp.bullets.forEach(bullet => {
        if (bullet.quality === 'strong') strongBullets++
        else if (bullet.quality === 'weak') weakBullets++
        else needsWorkBullets++
      })
    })

    const experienceComplete = structured.experience.length > 0
    const experienceCount = structured.experience.length

    // Skills completeness
    const skillsMissing: string[] = []
    if (structured.skills.languages.length === 0) skillsMissing.push('languages')
    if (structured.skills.frameworks.length === 0) skillsMissing.push('frameworks')
    if (structured.skills.tools.length === 0) skillsMissing.push('tools')
    const skillsComplete = skillsMissing.length === 0

    // Calculate overall completion percentage
    const weights = {
      target: 15,
      contact: 20,
      education: 15,
      experience: 35,
      skills: 15,
    }

    let totalScore = 0
    if (targetComplete) totalScore += weights.target
    else totalScore += weights.target * (1 - targetMissing.length / 4)

    if (contactComplete) totalScore += weights.contact
    else totalScore += weights.contact * (1 - contactMissing.length / 4)

    if (educationComplete) totalScore += weights.education

    if (experienceComplete) {
      // Experience quality affects score
      const totalBullets = strongBullets + needsWorkBullets + weakBullets
      if (totalBullets > 0) {
        const qualityScore = (strongBullets + needsWorkBullets * 0.5) / totalBullets
        totalScore += weights.experience * qualityScore
      } else {
        totalScore += weights.experience * 0.3 // Has experience but no bullets yet
      }
    }

    if (skillsComplete) totalScore += weights.skills
    else totalScore += weights.skills * (1 - skillsMissing.length / 3)

    return {
      overall: Math.round(totalScore),
      sections: {
        target: {
          complete: targetComplete,
          missing: targetMissing,
        },
        contact: {
          complete: contactComplete,
          missing: contactMissing,
        },
        education: {
          complete: educationComplete,
          count: educationCount,
        },
        experience: {
          complete: experienceComplete,
          count: experienceCount,
          strongBullets,
          weakBullets,
          needsWorkBullets,
        },
        skills: {
          complete: skillsComplete,
          missing: skillsMissing,
        },
      },
    }
  }, [memory])

  return {
    memory,
    structuredMemory,
    setMemory,
    updateMemory,
    resetMemory,
    getCompletionStatus,
  }
}
